'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FcGoogle } from 'react-icons/fc'

import { X } from 'lucide-react'

import { loginWithProvider } from '../../lib/auth'
import { isZitadelAuth } from '../../lib/auth-provider-config'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const zitadel = isZitadelAuth()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen || !mounted) return null

  const handleSignIn = () => {
    if (!zitadel) {
      localStorage.setItem('_cs_auth_redirect', window.location.pathname + window.location.search)
    }
    loginWithProvider('google')
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-kinetic-surface/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div
        ref={modalRef}
        className={cn(
          'kinetic-glass-panel relative w-full max-w-md animate-fade-in-up',
          'border border-kinetic-outline-variant/40 bg-kinetic-surface-container-highest/95 p-6 shadow-2xl backdrop-blur-xl'
        )}
      >
        <span
          className="pointer-events-none absolute left-0 top-0 border-kinetic-primary-container"
          style={{ width: 12, height: 12, borderLeftWidth: 2, borderTopWidth: 2 }}
          aria-hidden
        />
        <span
          className="pointer-events-none absolute right-0 top-0 border-kinetic-primary-container"
          style={{ width: 12, height: 12, borderRightWidth: 2, borderTopWidth: 2 }}
          aria-hidden
        />

        <div className="mb-6 flex items-center justify-between">
          <h3
            id="auth-modal-title"
            className="font-headline text-lg font-bold uppercase tracking-[0.12em] text-kinetic-on-surface"
          >
            Welcome back
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-kinetic-on-surface-variant transition-colors hover:bg-kinetic-surface-container hover:text-kinetic-on-surface"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5">
          <p className="font-body text-sm leading-relaxed text-kinetic-on-surface-variant">
            {zitadel
              ? 'Sign in with your Cloud Shuttle account — email, Google, or passkey on our secure login page.'
              : 'Choose a provider to sign in with your existing account.'}
          </p>

          {zitadel ? (
            <Button type="button" variant="kinetic" size="lg" className="w-full" onClick={handleSignIn}>
              Continue to sign in
            </Button>
          ) : (
            <button
              type="button"
              onClick={handleSignIn}
              className={cn(
                'flex w-full items-center justify-center gap-3 rounded-none border border-kinetic-outline-variant/50',
                'bg-kinetic-surface-container/60 px-5 py-3 transition-colors',
                'hover:border-kinetic-primary-container/40 hover:bg-kinetic-surface-container'
              )}
            >
              <FcGoogle size={22} />
              <span className="font-headline text-xs font-semibold uppercase tracking-[0.14em] text-kinetic-on-surface">
                Continue with Google
              </span>
            </button>
          )}
        </div>

        <p className="mt-8 border-t border-kinetic-outline-variant/30 pt-5 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-kinetic-outline">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>,
    document.body
  )
}
